import React, {useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBell, faChevronRight, faEye, faPlus, faSearch} from '@fortawesome/free-solid-svg-icons';
import {Alert, Button, Card, Container, Dropdown, Form, InputGroup, Modal, Navbar, Spinner} from '@themesberg/react-bootstrap';
import {FormattedMessage} from 'react-intl';
import {useHistory, useLocation} from 'react-router-dom';

import {Routes} from '../routes';
import Utils from '../services/Utils';
import API from '../services/API';
import {useNotifications} from '../services/NotificationsContext';
import {usePlan} from '../services/PlanContext';
import {API_URL} from '../services/constants';

// When you add a route, add its id here.
const BREADCRUMB_KEYS = {
  [Routes.DashboardOverview.path]: 'DASHBOARD',
  [Routes.Orders.path]:            'BOOKINGS',
  [Routes.Customers.path]:         'CUSTOMERS',
  [Routes.Planning.path]:          'PLANNING',
  [Routes.Settings.path]:          'SETTINGS',
  [Routes.Vehicles.path]:          'VEHICLES',
  [Routes.Packages.path]:          'PACKAGES',
  [Routes.Surcharges.path]:        'SURCHARGES',
  [Routes.Options.path]:           'OPTIONS',
  [Routes.DiscountCodes.path]:     'DISCOUNT_CODES',
  [Routes.BookingFields.path]:     'BOOKING_PAGE',
  [Routes.HtmlIntegration.path]:   'HTML_INTEGRATION',
};

const MODULE_PATHS = new Set([
  Routes.Settings.path, Routes.Vehicles.path, Routes.Packages.path,
  Routes.Surcharges.path, Routes.Options.path, Routes.DiscountCodes.path,
  Routes.BookingFields.path, Routes.HtmlIntegration.path,
]);

export default (props) => {
  const {intl, onCreateBooking, onOpenBooking} = props;
  const history = useHistory();
  const location = useLocation();
  const {unseenNotificationsCount, notifications, markNotificationsSeen} = useNotifications();
  const {plan: currentPlan} = usePlan();
  const [showSubscriptionAlert, setShowSubscriptionAlert] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleIdentifier, setModuleIdentifier] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    if (!Utils.isAccessToken(history, true)) return;
    API.getCheckSubscription(history).then(response => {
      if (response && response.data.success === true) {
        if (response.data.data && (new Date().getTime() + 86_400_000) > new Date(response.data.data).getTime()) {
          setShowSubscriptionAlert(true);
        }
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'UNABLE_TO_CHECK_YOUR_SUBSCRIPTION'}));
      }
    });
  }, []);

  // La bannière « abonnement expiré » est armée une seule fois au montage (getCheckSubscription). Quand le
  // chauffeur bascule en formule gratuite EN PLACE (sans rechargement), le plan du contexte passe à FREE
  // après refresh() : on masque alors la bannière immédiatement, au lieu d'attendre un changement de page.
  useEffect(() => {
    if (currentPlan === 'FREE') setShowSubscriptionAlert(false);
  }, [currentPlan]);

  const handleClickRenewMySubscription = () => {
    API.getStripeRenewSubscriptionUrl(history).then(response => {
      if (response && response.data.success === true) {
        window.location.replace(response.data.data);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  };

  const handlePreviewModule = () => {
    if (!moduleIdentifier) {
      API.getModule(history).then(response => {
        if (response && response.data.success === true) {
          setModuleIdentifier(response.data.data.identifier);
        }
        setShowModuleModal(true);
      });
    } else {
      setShowModuleModal(true);
    }
  };

  const getResponsiveModuleWidth  = (w) => Utils.isMobile() ? window.innerWidth - 82  : w;
  const getResponsiveModuleHeight = (h) => Utils.isMobile() ? window.innerHeight - 200 : h;

  // Breadcrumb: [Accueil] > [Page courante] ; si on est dans Module, on ajoute "Module".
  const pageKey = BREADCRUMB_KEYS[location.pathname];
  const inModule = MODULE_PATHS.has(location.pathname);

  // Debounced search — fires 300ms after the user stops typing. Shorter queries
  // are short-circuited server-side (min 2 chars) but we also skip the request
  // client-side to avoid the round-trip.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const timer = setTimeout(() => {
      API.getSearch(history, q).then(response => {
        if (response && response.data && response.data.success === true) {
          setSearchResults(response.data.data.bookings || []);
        } else {
          setSearchResults([]);
        }
        setSearchLoading(false);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close the results dropdown when the user clicks outside the search area.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Submit picks the first result if any.
    if (searchResults.length > 0) {
      handlePickResult(searchResults[0]);
    }
  };

  const handlePickResult = (result) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    // Fetch the full booking and open the layout-level BookingModal in place —
    // no navigation, works from any admin page.
    API.getAdminBooking(history, result.id).then(response => {
      if (response && response.data.success === true && onOpenBooking) {
        onOpenBooking(response.data.data);
      }
    });
  };

  const formatPrice = (price, currency) => {
    if (price == null || !currency) return '';
    try {
      return new Intl.NumberFormat(navigator.language, {style: 'currency', currency}).format(price);
    } catch (e) {
      return price + ' ' + currency;
    }
  };

  return (
    <>
      <Navbar variant="dark" expanded className="ccv2-topbar pb-0">
        <Container fluid className="ccv2-topbar-inner">

          {/* Left: breadcrumb */}
          <nav className="ccv2-breadcrumb d-none d-md-flex" aria-label="breadcrumb">
            <span className="ccv2-breadcrumb-item muted">
              <FormattedMessage id="BREADCRUMB_HOME" defaultMessage="Accueil"/>
            </span>
            {inModule && (
              <>
                <FontAwesomeIcon icon={faChevronRight} className="ccv2-breadcrumb-sep"/>
                <span className="ccv2-breadcrumb-item muted">
                  <FormattedMessage id="MODULE"/>
                </span>
              </>
            )}
            {pageKey && (
              <>
                <FontAwesomeIcon icon={faChevronRight} className="ccv2-breadcrumb-sep"/>
                <span className="ccv2-breadcrumb-item active">
                  <FormattedMessage id={pageKey}/>
                </span>
              </>
            )}
          </nav>

          <div className="ccv2-topbar-spacer"/>

          {/* Right: search + actions */}
          <div className="ccv2-topbar-actions">
            <div ref={searchContainerRef} className="ccv2-search-wrapper d-flex d-md-none d-lg-flex">
              <Form onSubmit={handleSearchSubmit} className="ccv2-search">
                <InputGroup>
                  <InputGroup.Text>
                    {searchLoading
                      ? <Spinner animation="border" size="sm" role="status"/>
                      : <FontAwesomeIcon icon={faSearch}/>}
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={intl.formatMessage({id: 'SEARCH_PLACEHOLDER', defaultMessage: 'Rechercher…'})}
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                    onFocus={() => setSearchOpen(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false); }}
                  />
                </InputGroup>
              </Form>

              {searchOpen && searchQuery.trim().length >= 2 && (
                <div className="ccv2-search-results">
                  {searchLoading && searchResults.length === 0 && (
                    <div className="ccv2-search-empty">
                      <FormattedMessage id="LOADING" defaultMessage="Chargement…"/>
                    </div>
                  )}
                  {!searchLoading && searchResults.length === 0 && (
                    <div className="ccv2-search-empty">
                      <FormattedMessage id="NO_RESULTS" defaultMessage="Aucun résultat"/>
                    </div>
                  )}
                  {searchResults.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      className="ccv2-search-result"
                      onClick={() => handlePickResult(r)}
                    >
                      <div className="ccv2-search-result-main">
                        <span className="ccv2-search-result-name">
                          {r.customerName || <FormattedMessage id="UNKNOWN" defaultMessage="Inconnu"/>}
                        </span>
                        <span className="ccv2-search-result-meta">{Utils.formatBookingNumber(r)}</span>
                      </div>
                      <div className="ccv2-search-result-sub">
                        {r.pickUpTime}
                        {r.vehicle ? ` · ${r.vehicle}` : ''}
                        {r.price != null && r.currency ? ` · ${formatPrice(r.price, r.currency)}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant="primary"
              className="ccv2-cta"
              title={intl.formatMessage({id: 'NEW_BOOKING', defaultMessage: 'Nouvelle réservation'})}
              onClick={() => onCreateBooking && onCreateBooking()}
            >
              <FontAwesomeIcon icon={faPlus} className="ccv2-cta-icon"/>
              <span className="ccv2-cta-text d-none d-md-inline">
                <FormattedMessage id="NEW_BOOKING" defaultMessage="Nouvelle réservation"/>
              </span>
            </Button>

            <Button
              variant="outline-secondary"
              className="ccv2-icon-btn"
              title={intl.formatMessage({id: 'MODULE_OVERVIEW'})}
              onClick={handlePreviewModule}
            >
              <FontAwesomeIcon icon={faEye}/>
            </Button>

            <Dropdown align="end" onToggle={(open) => { if (open) markNotificationsSeen(); }}>
              <Dropdown.Toggle as={Button} variant="outline-secondary" className="ccv2-icon-btn ccv2-bell">
                <FontAwesomeIcon icon={faBell}/>
                {unseenNotificationsCount > 0 && <span className="ccv2-bell-dot"/>}
              </Dropdown.Toggle>
              <Dropdown.Menu className="ccv2-notifications-menu">
                <div className="ccv2-notifications-header">
                  <FormattedMessage id="NOTIFICATIONS"/>
                </div>
                {notifications.length === 0 && (
                  <div className="ccv2-notifications-empty">
                    <FormattedMessage id="NO_NOTIFICATIONS" defaultMessage="Aucune notification"/>
                  </div>
                )}
                {notifications.map(n => (
                  <button
                    key={n.id}
                    type="button"
                    className="ccv2-notifications-item"
                    onClick={() => {
                      API.getAdminBooking(history, n.id).then(response => {
                        if (response && response.data.success === true && onOpenBooking) {
                          onOpenBooking(response.data.data);
                        }
                      });
                    }}
                  >
                    <div className="ccv2-notifications-item-main">
                      <span className="ccv2-notifications-item-name">
                        {n.customerName || <FormattedMessage id="UNKNOWN"/>}
                      </span>
                      <span className="ccv2-notifications-item-id">{Utils.formatBookingNumber(n)}</span>
                    </div>
                    <div className="ccv2-notifications-item-sub">
                      {n.pickUpTime}{n.vehicle ? ` · ${n.vehicle}` : ''}
                    </div>
                  </button>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Container>

        {showSubscriptionAlert && (
          <Alert variant="danger" className="ccv2-subscription-alert">
            <FormattedMessage id="SUBSCRIPTION_ALERT" values={{
              clickHere: <Card.Link onClick={handleClickRenewMySubscription} className="fw-bold">
                <FormattedMessage id="CLICK_HERE"/>
              </Card.Link>
            }}/>
          </Alert>
        )}
      </Navbar>

      {/* Module preview modal (déplacé de Sidebar.js) */}
      <Modal size="lg" as={Modal.Dialog} centered show={showModuleModal} onHide={() => setShowModuleModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="MODULE_OVERVIEW"/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowModuleModal(false)}/>
        </Modal.Header>
        <Modal.Body className="td-center">
          <div dangerouslySetInnerHTML={{
            __html: '<iframe src=\'' + API_URL + '/module/' + moduleIdentifier + '?admin=true\' width="' +
              getResponsiveModuleWidth(700) + '" height="' + getResponsiveModuleHeight(450) + '"></iframe>'
          }}/>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowModuleModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
